/** @module @liquidefy/code-editor/global/global-performance @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  PerformanceSnapshot,
  PerformanceBudget,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  now,
  emptyPerformanceSnapshot,
} from '../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 LOCAL TYPES — PerformanceState, LongTaskEntry, MemoryPressure, ScheduledWork, Metric
// ═══════════════════════════════════════════════════════════════════════════════

/** A recorded long-task entry. */
export interface LongTaskEntry {
  readonly id: string;
  readonly name: string;
  readonly durationMs: number;
  readonly timestamp: number;
  readonly exceedsBudgetBy: number;
}

/** Memory pressure level enumeration. */
export type MemoryPressure = 'none' | 'low' | 'medium' | 'high' | 'critical';

/** A piece of work scheduled for execution. */
export interface ScheduledWork {
  readonly id: string;
  readonly priority: 'idle' | 'low' | 'normal' | 'high' | 'critical';
  readonly scheduledAt: number;
  readonly work: string; // Descriptor — actual function is in IO layer
}

/** A named performance metric data point. */
export interface MetricEntry {
  readonly name: string;
  readonly value: number;
  readonly timestamp: number;
}

/** Budget utilization report. */
export interface FrameBudgetStatus {
  readonly budgetMs: number;
  readonly currentFrameMs: number;
  readonly utilizationPct: number;
  readonly isOverBudget: boolean;
  readonly recentFps: number;
}

/** Full performance state. */
export interface PerformanceState {
  readonly budget: PerformanceBudget;
  readonly snapshots: PerformanceSnapshot[];
  readonly longTasks: LongTaskEntry[];
  readonly scheduledWork: ScheduledWork[];
  readonly metrics: MetricEntry[];
  readonly lastFrameMs: number;
  readonly createdAt: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 DEFAULTS
// ═══════════════════════════════════════════════════════════════════════════════

/** Default performance budget: 60fps target, 16.7ms frame budget, 200MB memory. */
export const DEFAULT_PERFORMANCE_BUDGET: PerformanceBudget = {
  maxFrameTimeMs: 16.7,
  targetFps: 60,
  maxMemoryMb: 200,
  maxLongTaskMs: 50,
};

// ═══════════════════════════════════════════════════════════════════════════════
// §4 FACTORY — createPerformanceState
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Creates a new PerformanceState with an optional custom budget.
 * @param budget - Optional performance budget. Defaults to 60fps / 16.7ms / 200MB.
 * @returns Fresh PerformanceState with no recorded data.
 */
export function createPerformanceState(budget?: PerformanceBudget): PerformanceState {
  return {
    budget: budget ?? DEFAULT_PERFORMANCE_BUDGET,
    snapshots: [],
    longTasks: [],
    scheduledWork: [],
    metrics: [],
    lastFrameMs: 0,
    createdAt: now(),
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 FRAME TIMING — recordFrameTime, getFrameBudgetStatus
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Records a frame render duration, updating the last-frame field and taking a snapshot.
 * @param state - Current performance state.
 * @param durationMs - Duration of the frame in milliseconds.
 * @returns Updated PerformanceState.
 */
export function recordFrameTime(
  state: PerformanceState,
  durationMs: number
): PerformanceState {
  const fps = durationMs > 0 ? Math.min(1000 / durationMs, 120) : 0;

  const snapshot: PerformanceSnapshot = {
    timestamp: now(),
    fps,
    frameTimeMs: durationMs,
    longTasks: state.longTasks.length,
    memoryUsageMb: 0, // Populated by IO layer
    gcPauseMs: 0,
    renderQueueDepth: state.scheduledWork.length,
  };

  const MAX_SNAPSHOTS = 300; // Keep ~5 seconds at 60fps
  const snapshots = [...state.snapshots, snapshot];
  const trimmed = snapshots.length > MAX_SNAPSHOTS
    ? snapshots.slice(snapshots.length - MAX_SNAPSHOTS)
    : snapshots;

  return { ...state, lastFrameMs: durationMs, snapshots: trimmed };
}

/**
 * Returns the current frame budget utilization status.
 * @param state - Current performance state.
 * @returns FrameBudgetStatus.
 */
export function getFrameBudgetStatus(state: PerformanceState): FrameBudgetStatus {
  const budgetMs = state.budget.maxFrameTimeMs;
  const current = state.lastFrameMs;
  const utilization = budgetMs > 0 ? (current / budgetMs) * 100 : 0;

  // Compute rolling average FPS from recent snapshots
  const recent = state.snapshots.slice(-30);
  const recentFps = recent.length > 0
    ? recent.reduce((sum, s) => sum + s.fps, 0) / recent.length
    : 0;

  return {
    budgetMs,
    currentFrameMs: current,
    utilizationPct: Math.round(utilization * 10) / 10,
    isOverBudget: current > budgetMs,
    recentFps: Math.round(recentFps),
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 LONG TASKS — detectLongTask
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Checks if a task duration constitutes a "long task" and records it if so.
 * A task is long if it exceeds budget.maxLongTaskMs.
 * @param state - Current performance state (mutated by returning new state — caller must handle).
 * @param taskName - Descriptive name for the task.
 * @param durationMs - Duration of the task in ms.
 * @returns LongTaskEntry if the task was long, or null if within budget.
 */
export function detectLongTask(
  state: PerformanceState,
  taskName: string,
  durationMs: number
): LongTaskEntry | null {
  if (durationMs <= state.budget.maxLongTaskMs) return null;

  return {
    id: generateId('task-'),
    name: taskName,
    durationMs,
    timestamp: now(),
    exceedsBudgetBy: durationMs - state.budget.maxLongTaskMs,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 MEMORY — getMemoryPressure
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Determines the current memory pressure level based on the most recent snapshot.
 * @param state - Current performance state.
 * @returns MemoryPressure level.
 */
export function getMemoryPressure(state: PerformanceState): MemoryPressure {
  const latest = state.snapshots[state.snapshots.length - 1];
  if (!latest) return 'none';

  const maxMb = state.budget.maxMemoryMb;
  const usedMb = latest.memoryUsageMb;
  const ratio = usedMb / maxMb;

  if (ratio >= 0.95) return 'critical';
  if (ratio >= 0.80) return 'high';
  if (ratio >= 0.60) return 'medium';
  if (ratio >= 0.40) return 'low';
  return 'none';
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 WORK SCHEDULING — scheduleWork
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Schedules a work item with a given priority.
 * @param state - Current performance state.
 * @param work - Descriptor string for the work unit.
 * @param priority - Execution priority.
 * @returns ScheduledWork entry (caller must update state).
 */
export function scheduleWork(
  state: PerformanceState,
  work: string,
  priority: ScheduledWork['priority']
): ScheduledWork {
  return {
    id: generateId('work-'),
    priority,
    scheduledAt: now(),
    work,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §9 SNAPSHOT — getPerformanceSnapshot
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Returns the most recent performance snapshot, or an empty one if none recorded.
 * @param state - Current performance state.
 * @returns The latest PerformanceSnapshot.
 */
export function getPerformanceSnapshot(state: PerformanceState): PerformanceSnapshot {
  return state.snapshots[state.snapshots.length - 1] ?? emptyPerformanceSnapshot();
}

// ═══════════════════════════════════════════════════════════════════════════════
// §10 METRICS — recordMetric, getMetrics, clearOldMetrics
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Records a named numeric metric data point.
 * @param state - Current performance state.
 * @param name - Metric name.
 * @param value - Numeric value to record.
 * @returns Updated PerformanceState with the new metric appended.
 */
export function recordMetric(
  state: PerformanceState,
  name: string,
  value: number
): PerformanceState {
  const entry: MetricEntry = { name, value, timestamp: now() };
  return { ...state, metrics: [...state.metrics, entry] };
}

/**
 * Retrieves recorded metrics, optionally filtered by name.
 * @param state - Current performance state.
 * @param name - Optional metric name to filter by.
 * @returns Array of matching MetricEntry entries.
 */
export function getMetrics(state: PerformanceState, name?: string): MetricEntry[] {
  if (!name) return state.metrics;
  return state.metrics.filter((m) => m.name === name);
}

/**
 * Removes metric entries older than the specified age.
 * @param state - Current performance state.
 * @param olderThanMs - Age threshold in milliseconds.
 * @returns Updated PerformanceState with old metrics pruned.
 */
export function clearOldMetrics(
  state: PerformanceState,
  olderThanMs: number
): PerformanceState {
  const cutoff = now() - olderThanMs;
  const filtered = state.metrics.filter((m) => m.timestamp >= cutoff);
  const also = state.snapshots.filter((s) => s.timestamp >= cutoff);
  const tasks = state.longTasks.filter((t) => t.timestamp >= cutoff);
  return { ...state, metrics: filtered, snapshots: also, longTasks: tasks };
}
